import { useSelectionContext } from 'modules/customEvents/contexts';
import { INGESTION_POLICIES } from 'modules/customEvents/types';
import { ReactNode, useCallback, useEffect } from 'react';
import { ALL_TIME } from 'ui/datePicker/helpers';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useSortingContext } from 'utils/contexts/sorting';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import {
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { CONTENT_SECTIONS, SEARCH_PARAM_KEYS, SORTING_CONTEXT_INITIAL_STATE } from '../constants';
import { SearchParamsContext, initialSearchParamsState } from './SearchParamsContext';

export const SearchParamsContextProvider = ({ children }: { children: ReactNode }) => {
  const { sortBy, setSortBy, sortDirection, setSortDirection } = useSortingContext();
  const { deselectAll } = useSelectionContext();
  const { setCurrentPage } = usePaginationContext();

  /**
   * Defines which section of the page you are viewing
   * Content section: 'custom events' | 'user fields'
   */
  const [contentSection, setContentSection] = useSearchParamState({
    pushChangesToBrowserHistory: true,
    searchParamKey: SEARCH_PARAM_KEYS.contentSection,
    stateValueWhenSearchParamIsUnset: CONTENT_SECTIONS.customEvents,
    ...serializationOptionsForStringValue,
  });

  /**
   * Search term filter
   */
  const [searchParam, setSearchParam] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.search,
    stateValueWhenSearchParamIsUnset: '',
    pushChangesToBrowserHistory: true,
    ...serializationOptionsForStringValue,
  });
  const [debouncedSearch, search, setSearch] = useDebouncedState(searchParam, 300);
  useEffect(() => setSearchParam(search), [debouncedSearch]);

  /**
   * Visibility tab filters
   * User profile fields that are either `visible` or `hidden`
   */
  const [visibility, setVisibilityBase] = useSearchParamState({
    searchParamKey: 'visibility',
    stateValueWhenSearchParamIsUnset: [''],
    ...serializationOptionsForStringArrayValue,
  });

  // set pagination to first page when changing visibility filters
  const setVisibility = (val: string[]) => {
    setCurrentPage(1);
    setVisibilityBase(val);
  };

  /**
   * Visibility tab filters
   * User profile fields that are either `visible` or `hidden`
   */
  const [journeyTriggers, setJourneyTriggersBase] = useSearchParamState({
    searchParamKey: 'journeyTriggers',
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });

  // set pagination to first page when changing journeyTriggers filters
  const setJourneyTriggers = (val: string[]) => {
    setCurrentPage(1);
    setJourneyTriggersBase(val);
  };

  /**
   * Ingestion Policy for custom events
   *
   * NOTE:
   * Some components that are imported from Custom Events module rely on FilterContextProvider
   * We are tracking the ingestion policy here as well, so this useEffect syncs those two states
   *
   * An alternative is to remove any dependency on context within those components and
   * pass the values in as props from the parent where possible.
   */
  const [ingestionPolicy, setIngestionPolicy] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.ingestionPolicy,
    stateValueWhenSearchParamIsUnset: INGESTION_POLICIES.SAVED,
    ...serializationOptionsForStringValue,
  });

  /**
   * Date picker
   */
  const [startDate, setStartDate] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.startDate,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  const [endDate, setEndDate] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.endDate,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  const [preset, setPreset] = useSearchParamState({
    searchParamKey: SEARCH_PARAM_KEYS.preset,
    stateValueWhenSearchParamIsUnset: ALL_TIME,
    ...serializationOptionsForStringValue,
  });

  /**
   * Reset function to run when switching between sections
   * Accepts a `contentSection` argument to determine which section is currently active
   * since certain params only pertain to certain content sections
   */
  const resetSearchParams = useCallback(() => {
    deselectAll();
    setCurrentPage(1);
    setEndDate(initialSearchParamsState.endDate);
    setIngestionPolicy(initialSearchParamsState.ingestionPolicy);
    setJourneyTriggers(initialSearchParamsState.journeyTriggers);
    setPreset(initialSearchParamsState.preset);
    setSearch(initialSearchParamsState.search);
    setSortBy(SORTING_CONTEXT_INITIAL_STATE.sortBy);
    setSortDirection(SORTING_CONTEXT_INITIAL_STATE.sortDirection);
    setStartDate(initialSearchParamsState.startDate);
    setVisibility(initialSearchParamsState.visibility);
  }, [initialSearchParamsState]);

  const context = {
    contentSection,
    debouncedSearch,
    endDate,
    ingestionPolicy,
    journeyTriggers,
    preset,
    resetSearchParams,
    search,
    setContentSection,
    setEndDate,
    setIngestionPolicy,
    setJourneyTriggers,
    setPreset,
    setSearch,
    setSortBy,
    setSortDirection,
    setStartDate,
    setVisibility,
    sortBy,
    sortDirection,
    startDate,
    visibility,
  };

  useEffect(() => {
    // reset to first page when changing search params
    setCurrentPage(1);
  }, [debouncedSearch, ingestionPolicy, startDate, endDate, preset]);

  return <SearchParamsContext.Provider value={context}>{children}</SearchParamsContext.Provider>;
};
